// Explicit per-tab opt-in. No audio, text, device IDs or account IDs leave the browser.
export function microphoneDiagnosticsEnabled() {
  try { return sessionStorage.getItem('scribe.audioDiagnostics') === '1'; } catch (_) { return false; }
}
export function audioDiagnostic(stage, values = {}) {
  if (!microphoneDiagnosticsEnabled()) return;
  const allowed = ['state', 'enabled', 'muted', 'matched', 'sampleRate', 'channelCount', 'bytes', 'mimeType', 'peak', 'samples', 'duration', 'errorName', 'reason', 'code'];
  const safe = Object.fromEntries(Object.entries(values).filter(([key, value]) => allowed.includes(key) && ['string', 'number', 'boolean'].includes(typeof value)));
  console.info('[audio-diagnostic]', JSON.stringify({ stage, time: new Date().toISOString(), ...safe }));
}
export function observeAudioStream(stream, stage, selectedDeviceId) {
  if (!microphoneDiagnosticsEnabled()) return () => {};
  let context, source, analyser, interval, timeout, done = false, peak = 0, samples = 0;
  const track = stream?.getAudioTracks()[0];
  const state = () => {
    const settings = track?.getSettings?.() || {};
    audioDiagnostic(stage + ':track', { state: track?.readyState, enabled: track?.enabled, muted: track?.muted,
      matched: selectedDeviceId && settings.deviceId ? selectedDeviceId === settings.deviceId : undefined,
      sampleRate: settings.sampleRate, channelCount: settings.channelCount });
  };
  const stop = () => {
    if (done) return;
    done = true;
    clearInterval(interval); clearTimeout(timeout);
    audioDiagnostic(stage + ':signal', { peak, samples, state: context?.state });
    ['mute', 'unmute', 'ended'].forEach(event => track?.removeEventListener(event, state));
    source?.disconnect(); analyser?.disconnect();
    if (context) context.close().catch(() => {});
    // Never stop or change the application's audio tracks.
  };
  state();
  try {
    ['mute', 'unmute', 'ended'].forEach(event => track?.addEventListener(event, state));
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    context = new AudioContextClass();
    source = context.createMediaStreamSource(stream);
    analyser = context.createAnalyser();
    source.connect(analyser);
    const data = new Float32Array(analyser.fftSize);
    // Do not resume: report suspended state rather than changing application audio policy.
    interval = setInterval(() => {
      if (context.state !== 'running') return;
      analyser.getFloatTimeDomainData(data);
      for (const value of data) peak = Math.max(peak, Math.abs(value));
      samples++;
    }, 100);
    timeout = setTimeout(stop, 10000);
  } catch (error) {
    audioDiagnostic(stage + ':unavailable', { errorName: error.name });
    stop();
  }
  return stop;
}
export async function inspectRecordedAudio(blob) {
  if (!microphoneDiagnosticsEnabled()) return;
  audioDiagnostic('record:blob', { bytes: blob.size, mimeType: blob.type });
  // Short diagnostic recordings only; never decode a full clinical session into RAM.
  if (blob.size > 250000 || !blob.size) {
    audioDiagnostic('record:decode-skipped', { bytes: blob.size });
    return;
  }
  let context;
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    context = new AudioContextClass();
    const decoded = await context.decodeAudioData(await blob.arrayBuffer());
    let peak = 0;
    for (let channel = 0; channel < decoded.numberOfChannels; channel++) {
      for (const value of decoded.getChannelData(channel)) peak = Math.max(peak, Math.abs(value));
    }
    audioDiagnostic('record:decoded', { peak, duration: decoded.duration, sampleRate: decoded.sampleRate, channelCount: decoded.numberOfChannels });
  } catch (error) { audioDiagnostic('record:decode-failed', { errorName: error.name }); }
  finally { if (context) context.close().catch(() => {}); }
}
